import React, { useState, useEffect, useCallback } from 'react';

const STAGE_NAMES = { 1: 'Greenhouse', 2: 'Indoor', 3: 'Industrial', 4: 'Sandbox' };

const RANK_COLORS = {
  1: { color: '#d4a017', label: '🥇' },
  2: { color: '#9e9e9e', label: '🥈' },
  3: { color: '#cd7f32', label: '🥉' },
};

function PlayerCard({ entry, rank, tab }) {
  const rankStyle = RANK_COLORS[rank] || { color: 'var(--text-dim)', label: `#${rank}` };
  const isPrimary = rank <= 3;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '52px 1fr auto',
      gap: 14,
      alignItems: 'center',
      padding: '14px 18px',
      background: isPrimary ? 'rgba(212,160,23,0.04)' : 'var(--border-light)',
      border: `1px solid ${isPrimary ? rankStyle.color + '44' : 'var(--border)'}`,
      borderRadius: 'var(--radius)',
      marginBottom: 8,
    }}>
      {/* Rank */}
      <div style={{ textAlign: 'center' }}>
        {rank <= 3 ? (
          <div style={{ fontSize: 28, lineHeight: 1 }}>{rankStyle.label}</div>
        ) : (
          <div style={{ fontSize: 22, fontWeight: 800, color: rankStyle.color, fontFamily: 'monospace' }}>
            #{rank}
          </div>
        )}
      </div>

      {/* Info */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 3 }}>
          {entry.display_name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
          📅 Day {entry.game_day} &nbsp;·&nbsp; 🏢 {STAGE_NAMES[entry.stage] || `Stage ${entry.stage}`} &nbsp;·&nbsp; ⭐ Rep {entry.reputation}
        </div>
        {entry.best_review_quote && (
          <div style={{
            fontSize: 11,
            color: 'var(--text-dim)',
            fontStyle: 'italic',
            lineHeight: 1.4,
            borderLeft: `2px solid ${rankStyle.color}66`,
            paddingLeft: 8,
            marginTop: 4,
          }}>
            "{entry.best_review_quote}"
          </div>
        )}
        {entry.best_strain && (
          <div style={{
            display: 'inline-block',
            marginTop: 6,
            padding: '2px 8px',
            background: 'var(--green-dark)',
            border: '1px solid var(--green-dim)',
            borderRadius: 99,
            fontSize: 10,
            color: 'var(--green)',
          }}>
            🌿 {entry.best_strain}
          </div>
        )}
      </div>

      {/* Primary stat */}
      <div style={{ textAlign: 'right', minWidth: 90 }}>
        {tab === 'revenue' ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, color: rankStyle.color, fontFamily: 'monospace' }}>
              ${Number(entry.total_revenue).toLocaleString()}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>total revenue</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, color: rankStyle.color, fontFamily: 'monospace' }}>
              {entry.best_quality}<span style={{ fontSize: 12, color: 'var(--text-dim)' }}>/100</span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>best quality</div>
          </>
        )}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={{
      height: 88,
      background: 'var(--border-light)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      marginBottom: 8,
      opacity: 0.5,
      animation: 'pulse 1.5s ease-in-out infinite',
    }} />
  );
}

export function LeaderboardScreen({ state }) {
  const [tab, setTab] = useState('revenue');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/leaderboard');
      if (!res.ok) throw new Error('Failed to load');
      setData(await res.json());
    } catch (e) {
      setError('Could not load the leaderboard. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const entries = data?.[tab] ?? [];

  return (
    <div className="screen" style={{ maxWidth: 720, margin: '0 auto', padding: '0 4px' }}>

      {/* ── Magazine header ── */}
      <div style={{
        background: '#120f09',
        border: '1px solid #3a2e1a',
        borderRadius: 'var(--radius)',
        padding: '24px 28px 20px',
        marginBottom: 16,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative leaf pattern */}
        <div style={{
          position: 'absolute', top: -10, right: -10,
          fontSize: 80, opacity: 0.06, transform: 'rotate(20deg)', userSelect: 'none',
        }}>🌿</div>
        <div style={{
          position: 'absolute', bottom: -10, left: 20,
          fontSize: 60, opacity: 0.05, transform: 'rotate(-15deg)', userSelect: 'none',
        }}>🍃</div>

        {/* Masthead */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
          <span style={{
            fontSize: 28,
            fontWeight: 900,
            letterSpacing: '0.15em',
            color: '#d4a017',
            textTransform: 'uppercase',
            fontFamily: 'Georgia, serif',
          }}>CANNZINE</span>
          <span style={{ fontSize: 11, color: '#7a6030', letterSpacing: '0.05em' }}>
            VOL. 1 &nbsp;·&nbsp; ISSUE {state.gameWeek || 1} &nbsp;·&nbsp; DAY {state.gameDay}
          </span>
        </div>

        {/* Rule */}
        <div style={{ height: 2, background: 'linear-gradient(90deg, #d4a017, #3a2e1a)', marginBottom: 10 }} />

        {/* Headline */}
        <div style={{
          fontSize: 22,
          fontWeight: 800,
          color: '#e8d5a0',
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.02em',
          lineHeight: 1.2,
          marginBottom: 6,
        }}>
          Rising Stars in the Grow Scene
        </div>
        <div style={{ fontSize: 12, color: '#7a6030', fontStyle: 'italic' }}>
          The industry's top cultivators, ranked by the numbers that matter.
        </div>
      </div>

      {/* ── Tabs + Refresh ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        {[
          { id: 'revenue', label: '💰 Revenue Kings' },
          { id: 'quality', label: '🏆 Quality Champions' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={tab === t.id ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            style={{ fontSize: 12, padding: '5px 14px' }}
          >
            {t.label}
          </button>
        ))}
        <button
          onClick={fetchLeaderboard}
          className="btn btn-secondary btn-sm"
          style={{ marginLeft: 'auto', fontSize: 11 }}
          disabled={loading}
        >
          {loading ? '...' : '↻ Refresh'}
        </button>
      </div>

      {/* ── Board ── */}
      {error && (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--red)', fontSize: 13 }}>{error}</div>
      )}

      {loading && !data && (
        <> {[...Array(5)].map((_, i) => <SkeletonCard key={i} />)} </>
      )}

      {!loading && !error && entries.length === 0 && (
        <div style={{
          padding: '40px 20px', textAlign: 'center',
          color: 'var(--text-muted)', fontSize: 13,
          background: 'var(--border-light)', borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🌱</div>
          No entries yet. Be the first to make the board — sign in and start growing!
        </div>
      )}

      {entries.map((entry, i) => (
        <PlayerCard key={entry.user_id} entry={entry} rank={i + 1} tab={tab} />
      ))}

      {/* ── Footer note ── */}
      <div style={{
        marginTop: 16,
        padding: '10px 14px',
        background: 'var(--border-light)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        fontSize: 11,
        color: 'var(--text-dim)',
        textAlign: 'center',
        fontStyle: 'italic',
      }}>
        Leaderboard updates when signed-in players sync their saves. Sign in via the header to appear.
      </div>
    </div>
  );
}
